import { useCommissioningDeviceStore } from '../stores/commissioningDeviceStore';
import { MeterAppState } from '../models/MeterAppState';
import {
  MQTT_COMMAND,
  RECOMMISSION_PREPARE_STEPS,
  getNextRecommissionPrepareStepIndex,
  getRecommissionPrepareStepIndex,
  type RecommissionPrepareStepConfig,
  type RecommissionPrepareStepId,
} from './constants';

export type PrepareRecommissionOptions = {
  /** Called when a step becomes active (before delay + work). */
  onStep?: (step: RecommissionPrepareStepConfig) => void;
  /** Called with cumulative progress percent (0–100) as work completes. */
  onProgress?: (percent: number) => void;
  /** Optional override for sleep (tests). */
  delay?: (ms: number) => Promise<void>;
  /** Optional abort check; when true, stop without throwing. */
  isAborted?: () => boolean;
};

const defaultDelay = (ms: number) =>
  ms <= 0 ? Promise.resolve() : new Promise<void>((resolve) => setTimeout(resolve, ms));

function getStep(id: RecommissionPrepareStepId): RecommissionPrepareStepConfig {
  const step = RECOMMISSION_PREPARE_STEPS.find((entry) => entry.id === id);
  if (!step) {
    throw new Error(`Missing recommission prepare step config for "${id}"`);
  }
  return step;
}

/**
 * Client-orchestrated recommission prepare sequence (device state only).
 * Resolves after waitDecommissioned; caller hands off to SearchingSolshare
 * for findSolShare + config hydrate + routing.
 * Rejects on any step failure / timeout.
 */
export async function prepareRecommission(
  serialNumber: string,
  options: PrepareRecommissionOptions = {},
): Promise<void> {
  const store = useCommissioningDeviceStore();
  const delay = options.delay ?? defaultDelay;
  const { onStep, onProgress, isAborted } = options;

  const startIndex = getNextRecommissionPrepareStepIndex(
    store.recommissionPrepare.lastCompletedStepId,
  );

  if (startIndex >= RECOMMISSION_PREPARE_STEPS.length) {
    return;
  }

  let completedPercent = startIndex === 0 ? 0 : store.recommissionPrepare.progressPercent;

  const _reportProgress = (percent: number) => {
    completedPercent = Math.min(100, Math.max(0, percent));
    onProgress?.(completedPercent);
  };

  _reportProgress(completedPercent);

  if (startIndex === 0 && store.recommissionPrepare.status === 'idle') {
    store.updateRecommissionPrepare({
      status: 'in_progress',
      stepMessage: getStep(RECOMMISSION_PREPARE_STEPS[0].id).message,
    });
  }

  const _markStepComplete = (step: RecommissionPrepareStepConfig) => {
    const nextStep = RECOMMISSION_PREPARE_STEPS[getRecommissionPrepareStepIndex(step.id) + 1];
    store.updateRecommissionPrepare({
      lastCompletedStepId: step.id,
      progressPercent: completedPercent,
      stepMessage: nextStep?.message ?? step.message,
      status: 'in_progress',
    });
  };

  const _completeStepProgress = (step: RecommissionPrepareStepConfig) => {
    completedPercent = Math.min(100, completedPercent + step.progress);
    _reportProgress(completedPercent);
    _markStepComplete(step);
  };

  const _runStep = async (
    id: RecommissionPrepareStepId,
    work: (step: RecommissionPrepareStepConfig) => Promise<void>,
    skipDelay = false,
  ): Promise<void> => {
    if (isAborted?.()) {
      return;
    }

    const step = getStep(id);
    onStep?.(step);
    store.updateRecommissionPrepare({
      stepMessage: step.message,
      status: 'in_progress',
    });

    if (step.delayMs > 0 && !skipDelay) {
      await delay(step.delayMs);
    }

    if (isAborted?.()) {
      return;
    }

    await work(step);
  };

  for (let stepIndex = startIndex; stepIndex < RECOMMISSION_PREPARE_STEPS.length; stepIndex++) {
    const stepId = RECOMMISSION_PREPARE_STEPS[stepIndex].id;
    const skipDelay = stepIndex === startIndex && startIndex > 0;

    switch (stepId) {
      case 'actorPresent':
        await _runStep(
          'actorPresent',
          async (step) => {
            await store.sendMeterCommand(serialNumber, MQTT_COMMAND.ACTOR_PRESENT);
            _completeStepProgress(step);
          },
          skipDelay,
        );
        break;

      case 'commissionEnter':
        await _runStep(
          'commissionEnter',
          async (step) => {
            await store.sendMeterCommand(serialNumber, MQTT_COMMAND.COMMISSION_ENTER);
            _completeStepProgress(step);
          },
          skipDelay,
        );
        break;

      case 'waitPreCommissioned':
        await _runStep(
          'waitPreCommissioned',
          async (step) => {
            const base = completedPercent;
            let earned = 0;
            const pollStep = step.pollStep ?? step.progress;

            await store.pollUntilAppState(serialNumber, MeterAppState.PreCommissioned, () => {
              earned = Math.min(step.progress, earned + pollStep);
              _reportProgress(base + earned);
            });

            _reportProgress(base + step.progress);
            _markStepComplete(step);
          },
          skipDelay,
        );
        break;

      case 'commissionDecommission':
        await _runStep(
          'commissionDecommission',
          async (step) => {
            await store.sendMeterCommand(serialNumber, MQTT_COMMAND.COMMISSION_DECOMMISSION);
            _completeStepProgress(step);
          },
          skipDelay,
        );
        break;

      case 'waitDecommissioned':
        await _runStep(
          'waitDecommissioned',
          async (step) => {
            const base = completedPercent;
            let earned = 0;
            const pollStep = step.pollStep ?? step.progress;

            await store.pollUntilAllConnectionsDecommissioned(serialNumber, () => {
              earned = Math.min(step.progress, earned + pollStep);
              _reportProgress(base + earned);
            });

            _reportProgress(base + step.progress);
            _markStepComplete(step);
          },
          skipDelay,
        );
        break;

      default:
        throw new Error(`Unhandled recommission prepare step "${stepId}"`);
    }

    if (isAborted?.()) {
      return;
    }
  }
}
